import { useState } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { useHover } from "react-use";
import { Box, Fade, Checkbox, styled } from "@mui/material";
import Chip from "catalog/components/Chip";
import EntityTag from "catalog/components/EntityTag";
import MatchHighlighter from "catalog/components/MatchHighlighter";
import PinButton from "catalog/components/PinButton";
import PreferredBadge from "catalog/components/PreferredBadge";
import ResultDescription from "catalog/components/ResultDescription";
import ResultLabel from "catalog/components/ResultLabel";
import ResultMatches from "catalog/components/ResultMatches";
import { PREFERRED_ENTITIES_TAG } from "catalog/utils/buildFilterMatrix";
import GenerateCodeButton from "~/components/GenerateCodeButton";
import TextOverflow from "~/components/TextOverflow";
import { LineageButton } from "~/pages/datasets/components/LineageButton";
import { TableLineageModal } from "~/pages/datasets/components/TableLineageModal";
import { Highlight, VariableEntity } from "~/types/interfaces/catalogResponse";

type ResultProps = {
  entity: VariableEntity;
  highlight?: Highlight;
  selectable?: boolean;
  selected?: boolean;
  hoverable?: boolean;
  toggleSelected?: (checked: boolean) => void;
};

const StyledLink = styled(RouterLink)(({ theme }) => ({
  textDecoration: "none",
  fontWeight: 600,
  color: theme.palette.text.link,
  "&:visited": {
    color: theme.palette.text.visited,
  },
  "&:hover": {
    textDecoration: "underline",
  },
}));

const StyledName = styled(Box)({
  fontWeight: 600,
  color: "primary.main",
});

const actionButtonStyle = {
  fontSize: 13,
  padding: "4px 10px",
  "& .MuiButton-startIcon": { marginRight: "4px" },
  "& svg": { width: "16px" },
};

const VariableResult = ({
  entity,
  highlight,
  hoverable,
  selectable,
  selected,
  toggleSelected,
}: ResultProps) => {
  const { search } = useLocation();
  const { name, description, tags, dataType, fullyQualifiedName, table: linkedDataset } = entity;
  const [lineageModalOpen, setLineageModalOpen] = useState(false);
  const isPreferred = tags?.some((tag) => tag.tagFQN === PREFERRED_ENTITIES_TAG);
  const variableTags = tags?.filter((tag) => tag.tagFQN !== PREFERRED_ENTITIES_TAG);
  const tableTags = linkedDataset?.tags?.filter((tag) => tag.tagFQN !== PREFERRED_ENTITIES_TAG);
  const datasetName = linkedDataset?.name;

  const row = (hovered: boolean) => (
    <Box
      sx={{
        py: 2.5,
        gap: 0.75,
        display: "flex",
        flexDirection: "column",
        position: "relative",
        "&:not(:first-of-type)": {
          borderTop: "1px solid",
          borderTopColor: "grey.300",
        },
      }}
    >
      {/**
       * Selection & Pinning controls
       */}
      <Box
        sx={{
          position: "absolute",
          left: -40,
          top: 21,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minWidth: 38,
          height: 33,
        }}
      >
        {!selectable && <PinButton fqn={fullyQualifiedName} showPin={hovered} />}
        {selectable && toggleSelected && (
          <Checkbox
            checked={selected}
            size="small"
            onChange={(event) => toggleSelected(event.target.checked)}
          />
        )}
      </Box>

      <Box
        sx={{
          height: 33,
          fontSize: 16,
          fontWeight: 600,
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1.5,
        }}
      >
        <Box display="flex" gap={1} alignItems="center" overflow="hidden">
          <TextOverflow>
            <Box
              component={linkedDataset ? StyledLink : StyledName}
              to={{
                pathname: `/data-browser/tables/${linkedDataset.fullyQualifiedName}`,
                search: `${search}&field=${name}`,
              }}
              state={{ referrer: "columns" }}
            >
              <MatchHighlighter text={name} highlight={highlight?.name} />
            </Box>
          </TextOverflow>
          {isPreferred && <PreferredBadge />}
        </Box>

        {/**
         * Action buttons
         */}
        <Fade in={hovered} unmountOnExit>
          <Box display="flex" gap={1}>
            <LineageButton
              onOpenLineage={() => setLineageModalOpen(true)}
              analyticsContext={{
                table_fqn: linkedDataset?.fullyQualifiedName,
              }}
              analyticsEvent="column_search_lineage_clicked"
              sx={actionButtonStyle}
            />
            {datasetName && (
              <GenerateCodeButton
                sx={actionButtonStyle}
                selections={[
                  {
                    schema: linkedDataset.databaseSchema?.name,
                    table: datasetName,
                    variables: [entity.name],
                  },
                ]}
              />
            )}
          </Box>
        </Fade>
      </Box>

      {/**
       * Description
       */}
      {description && (
        <ResultDescription description={description} highlight={highlight?.description} />
      )}

      {/**
       * Dataset
       */}
      <Box display="flex" alignItems="center">
        <ResultLabel>Table</ResultLabel>
        <Box
          component={linkedDataset ? StyledLink : StyledName}
          to={{
            pathname: `/data-browser/tables/${linkedDataset.fullyQualifiedName}`,
            search: search,
          }}
          state={{ referrer: "columns" }}
          sx={{
            fontSize: 13,
          }}
        >
          <MatchHighlighter text={linkedDataset.name} highlight={highlight?.["table.name"]} />
        </Box>
      </Box>

      {/**
       * Datatype
       */}
      <Box display="flex" alignItems="center">
        <ResultLabel>Type</ResultLabel>
        <Box
          sx={{
            fontFamily: "monospace",
            textTransform: "lowercase",
            fontSize: "13px",
            px: "6px",
            py: "2px",
            borderRadius: "4px",
            color: `rgba(0, 0, 0 , 0.8)`,
            bgcolor: "grey.200",
          }}
        >
          {dataType}
        </Box>
      </Box>

      {/**
       * Variable tags
       */}
      {!!variableTags?.length && (
        <Box display="flex" alignItems="center">
          <ResultLabel>Column Tags</ResultLabel>
          <Box sx={{ display: "flex", gap: 0.5, flexFlow: "wrap" }}>
            {variableTags.map((tag) => (
              <Chip key={tag.tagFQN} sx={{ py: 0.5 }}>
                <EntityTag tag={tag} />
              </Chip>
            ))}
          </Box>
        </Box>
      )}

      {/**
       * Dataset tags
       */}
      {!!tableTags?.length && (
        <Box display="flex" alignItems="baseline">
          <ResultLabel>Table Tags</ResultLabel>
          <Box sx={{ display: "flex", gap: 0.5, flexFlow: "wrap" }}>
            {tableTags.map((tag) => (
              <Chip key={tag.tagFQN} sx={{ py: 0.5 }}>
                <EntityTag tag={tag} />
              </Chip>
            ))}
          </Box>
        </Box>
      )}

      {highlight && <ResultMatches highlight={highlight} />}
      <TableLineageModal
        open={lineageModalOpen}
        onOpenChange={setLineageModalOpen}
        tableFQN={linkedDataset?.fullyQualifiedName}
        tableName={linkedDataset?.name}
      />
    </Box>
  );

  const [hoverableRow] = useHover(row);
  return hoverable ? hoverableRow : row(false);
};

export default VariableResult;
